<script setup>
import { ref, onMounted } from "vue";
import Swal from "sweetalert2";
import api from "../../api/axios";
import ArgonButton from "@/components/ArgonButton.vue";
import ArgonInput from "@/components/ArgonInput.vue";

const companies = ref([]);
const cargando = ref(true);
const guardando = ref(false);
const showModal = ref(false);

const form = ref({
  name: "",
  slug: "",
  adminName: "",
  adminEmail: ""
});

const TODOS_LOS_MODULOS = [
  { value: "crm", label: "CRM (contactos, empresas, embudo, tareas, agenda)" },
  { value: "communication", label: "Comunicación (WhatsApp, Instagram, Messenger)" },
  { value: "operations", label: "Cotizador" },
  { value: "ai_lab", label: "Laboratorio de IA" }
];

const showModulesModal = ref(false);
const empresaModulos = ref(null);
const modulosSeleccionados = ref([]);
const esOverride = ref(false);
const guardandoModulos = ref(false);

async function getCompanies() {
  const res = await api.get("/platform/companies");
  companies.value = res.data.companies;
}

function slugify(texto) {
  return texto
    .toLowerCase()
    .normalize("NFD").replace(/\p{Diacritic}/gu, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function onNombreInput() {
  form.value.slug = slugify(form.value.name);
}

async function crearEmpresa() {
  if (!form.value.name || !form.value.slug || !form.value.adminEmail) {
    Swal.fire({ icon: "warning", title: "Faltan datos", text: "Nombre, slug y email del admin son obligatorios." });
    return;
  }

  guardando.value = true;

  try {
    await api.post("/platform/companies", form.value);
    showModal.value = false;
    form.value = { name: "", slug: "", adminName: "", adminEmail: "" };
    await getCompanies();

    Swal.fire({
      icon: "success",
      title: "Empresa creada",
      text: "Se envió un correo al admin para que configure su contraseña.",
      timer: 3000,
      showConfirmButton: false
    });

  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo crear la empresa",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  } finally {
    guardando.value = false;
  }
}

async function abrirModulos(company) {
  empresaModulos.value = company;

  try {
    const res = await api.get(`/platform/companies/${company.id}/modules`);
    modulosSeleccionados.value = [...res.data.effectiveModules];
    esOverride.value = res.data.override !== null;
    showModulesModal.value = true;
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudieron cargar los módulos",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  }
}

function toggleModulo(valor) {
  const idx = modulosSeleccionados.value.indexOf(valor);
  if (idx === -1) modulosSeleccionados.value.push(valor);
  else modulosSeleccionados.value.splice(idx, 1);
}

async function guardarModulos() {
  guardandoModulos.value = true;

  try {
    await api.put(`/platform/companies/${empresaModulos.value.id}/modules`, {
      modules: modulosSeleccionados.value
    });
    showModulesModal.value = false;

    Swal.fire({ icon: "success", title: "Módulos actualizados", timer: 1500, showConfirmButton: false });

  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudieron guardar los módulos",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  } finally {
    guardandoModulos.value = false;
  }
}

async function restablecerModulosAlPlan() {
  guardandoModulos.value = true;

  try {
    await api.put(`/platform/companies/${empresaModulos.value.id}/modules`, { modules: null });
    showModulesModal.value = false;

    Swal.fire({ icon: "success", title: "Módulos restablecidos al plan", timer: 1500, showConfirmButton: false });

  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo restablecer",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  } finally {
    guardandoModulos.value = false;
  }
}

function formatFecha(fecha) {
  return fecha ? new Date(fecha).toLocaleDateString("es-CO") : "—";
}

function estadoLabel(status) {
  return {
    trial: "En prueba",
    active: "Activa",
    past_due: "Vencida",
    cancelled: "Cancelada"
  }[status] || "Sin suscripción";
}

function estadoBadgeClass(status) {
  return {
    trial: "bg-info text-dark",
    active: "bg-success",
    past_due: "bg-danger",
    cancelled: "bg-gradient-secondary"
  }[status] || "bg-secondary";
}

onMounted(async () => {
  cargando.value = true;
  await getCompanies();
  cargando.value = false;
});
</script>

<template>
  <div class="container-fluid py-4">

    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="mb-0">
        <i class="bi bi-buildings me-2"></i>
        Empresas del SaaS
      </h4>

      <argon-button color="success" @click="showModal = true">
        <i class="bi bi-plus-circle me-1"></i>
        Nueva empresa
      </argon-button>
    </div>

    <div v-if="cargando" class="text-center py-5">
      <div class="spinner-border text-success" role="status"></div>
    </div>

    <div v-else class="card">
      <div class="card-body table-responsive">
        <table class="table table-striped">
          <thead>
            <tr>
              <th>Empresa</th>
              <th>Slug</th>
              <th>Usuarios activos</th>
              <th>Cotizaciones</th>
              <th>Suscripción</th>
              <th>Alta</th>
              <th>Módulos</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in companies" :key="c.id">
              <td>{{ c.name }}</td>
              <td>{{ c.slug }}</td>
              <td>{{ c.active_users }}</td>
              <td>{{ c.total_quotes }}</td>
              <td>
                <span class="badge" :class="estadoBadgeClass(c.subscription_status)">
                  {{ estadoLabel(c.subscription_status) }}
                </span>
              </td>
              <td>{{ formatFecha(c.createdAt) }}</td>
              <td>
                <button class="btn btn-sm btn-outline-primary" @click="abrirModulos(c)">
                  <i class="bi bi-puzzle me-1"></i>Ver/editar
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- MODAL NUEVA EMPRESA -->
    <div v-if="showModal" class="modal fade show d-block">
      <div class="modal-dialog">
        <div class="modal-content">

          <div class="modal-header">
            <h5><i class="bi bi-building-add"></i> Nueva empresa</h5>
            <button class="btn-close" @click="showModal = false"></button>
          </div>

          <div class="modal-body">
            <div class="mb-3">
              <label>Nombre de la empresa</label>
              <argon-input v-model="form.name" @input="onNombreInput" placeholder="Agencia Viajes XYZ" />
            </div>

            <div class="mb-3">
              <label>Slug (identificador único)</label>
              <argon-input v-model="form.slug" placeholder="agencia-viajes-xyz" />
            </div>

            <div class="mb-3">
              <label>Nombre del admin inicial</label>
              <argon-input v-model="form.adminName" />
            </div>

            <div class="mb-3">
              <label>Email del admin inicial</label>
              <argon-input v-model="form.adminEmail" placeholder="admin@agencia.com" />
              <small class="text-muted">Recibirá un correo para configurar su contraseña.</small>
            </div>
          </div>

          <div class="modal-footer">
            <button class="btn btn-secondary" @click="showModal = false">Cancelar</button>
            <button class="btn btn-success" :disabled="guardando" @click="crearEmpresa">
              <i class="bi bi-save me-1"></i>
              Crear empresa
            </button>
          </div>

        </div>
      </div>
    </div>

    <div v-if="showModal" class="modal-backdrop fade show"></div>

    <!-- MODAL MÓDULOS -->
    <div v-if="showModulesModal" class="modal fade show d-block">
      <div class="modal-dialog">
        <div class="modal-content">

          <div class="modal-header">
            <h5><i class="bi bi-puzzle"></i> Módulos de {{ empresaModulos?.name }}</h5>
            <button class="btn-close" @click="showModulesModal = false"></button>
          </div>

          <div class="modal-body">
            <p class="text-muted small">
              Por defecto se usan los módulos incluidos en el plan contratado. Marcar/desmarcar acá crea
              un override manual para esta empresa específica (ej. para un trato a medida).
            </p>

            <div v-if="esOverride" class="alert alert-warning py-2 px-3 small">
              <i class="bi bi-exclamation-triangle me-1"></i>
              Esta empresa tiene un override manual activo (no está usando los módulos del plan tal cual).
            </div>

            <div v-for="modulo in TODOS_LOS_MODULOS" :key="modulo.value" class="form-check mb-2">
              <input
                class="form-check-input"
                type="checkbox"
                :id="`modulo-${modulo.value}`"
                :checked="modulosSeleccionados.includes(modulo.value)"
                @change="toggleModulo(modulo.value)"
              >
              <label class="form-check-label" :for="`modulo-${modulo.value}`">{{ modulo.label }}</label>
            </div>
          </div>

          <div class="modal-footer justify-content-between">
            <button class="btn btn-outline-secondary btn-sm" :disabled="guardandoModulos" @click="restablecerModulosAlPlan">
              Restablecer al plan
            </button>
            <div class="d-flex gap-2">
              <button class="btn btn-secondary" @click="showModulesModal = false">Cancelar</button>
              <argon-button color="success" :disabled="guardandoModulos" @click="guardarModulos">Guardar</argon-button>
            </div>
          </div>

        </div>
      </div>
    </div>

    <div v-if="showModulesModal" class="modal-backdrop fade show"></div>

  </div>
</template>
